import { Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { BreadcrumbItem } from './breadcrumb.component';

@Injectable({ providedIn: 'root' })
export class BreadcrumbService {
  private baseItems$ = new BehaviorSubject<BreadcrumbItem[]>([]);
  private lastLabelOverride$ = new BehaviorSubject<string | null>(null);
  private extras$ = new BehaviorSubject<string[]>([]);

  readonly items$: Observable<BreadcrumbItem[]> = combineLatest([
    this.baseItems$,
    this.lastLabelOverride$,
    this.extras$,
  ]).pipe(
    map(([base, override, extras]) => {
      if (base.length === 0) {
        return [];
      }
      let items = base.slice();
      if (extras.length > 0) {
        items.push(...extras.map((label) => ({ label })));
      }
      if (override !== null) {
        const lastIndex = items.length - 1;
        items[lastIndex] = { ...items[lastIndex], label: override };
      }
      return items;
    })
  );

  constructor(private router: Router, private route: ActivatedRoute) {
    this.router.events
      .pipe(filter((e): e is NavigationStart => e instanceof NavigationStart))
      .subscribe(() => {
        this.lastLabelOverride$.next(null);
        this.extras$.next([]);
      });

    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => this.baseItems$.next(this.buildItems()));

    this.baseItems$.next(this.buildItems());
  }

  setLastLabel(label: string): void {
    this.lastLabelOverride$.next(label);
  }

  setExtras(...labels: string[]): void {
    this.extras$.next(labels.filter((l) => !!l));
  }

  private buildItems(): BreadcrumbItem[] {
    const items: BreadcrumbItem[] = [];
    let node: ActivatedRoute = this.route.root;
    let url = '';

    while (node.firstChild) {
      node = node.firstChild;
      const segs = node.snapshot.url.map((s) => s.path).join('/');
      if (segs) {
        url = url.endsWith('/') ? url + segs : url + '/' + segs;
      }
      const ownData = node.snapshot.routeConfig?.data;
      const label: string | undefined = ownData?.breadcrumb;
      if (label) {
        items.push({ label, link: url });
      }
    }

    if (items.length === 0) {
      return [];
    }

    items.unshift({ label: '홈', link: '/main' });
    return items;
  }
}
